import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Plus, Minus, Trash2, Clock, Check, Cpu, Coffee, ShoppingBag, Tag, ArrowRight, Loader2, AlertTriangle } from 'lucide-react';
import { useBookingConfig } from '@/lib/BookingConfigContext';
import WalkInRigPicker from '@/components/walkin/WalkInRigPicker';
import SessionExtensionTiles from '@/components/walkin/SessionExtensionTiles';
import SessionExtensionLines from '@/components/walkin/SessionExtensionLines';
import WalkInSizePicker from '@/components/walkin/WalkInSizePicker';
import { extensionsTotal as sumExtensions } from '@/lib/sessionExtensions';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function currentSlot() {
  const h = new Date().getHours();
  const clamped = Math.min(20, Math.max(9, h));
  return `${String(clamped).padStart(2, '0')}:00`;
}

const CAT_META = {
  drink: { label: 'DRINKS', icon: Coffee, tint: 'text-sky-300 border-sky-500/20 bg-sky-500/[0.04]' },
  snack: { label: 'SNACKS', icon: ShoppingBag, tint: 'text-amber-300 border-amber-500/20 bg-amber-500/[0.04]' },
  merch: { label: 'MERCH', icon: Tag, tint: 'text-emerald-300 border-emerald-500/20 bg-emerald-500/[0.04]' },
};
const CAT_ORDER = ['drink', 'snack', 'merch'];

// Square reporting-category → POS section. Concession tiles are sourced live from
// Square products by their reporting category (Beverages / Snacks / Merch).
const RCAT_RULES = [
  { re: /beverage/i, cat: 'drink' },
  { re: /snack/i, cat: 'snack' },
  { re: /merch/i, cat: 'merch' },
];
function posCatFor(reportingCategory) {
  if (!reportingCategory) return null;
  const m = RCAT_RULES.find((r) => r.re.test(reportingCategory));
  return m ? m.cat : null;
}

// A variation is sellable while Square still holds stock for it. A null figure
// means Square isn't tracking it, so it is treated as available.
function isSellable(product) {
  return product.in_stock !== false && !(product.stock_quantity != null && product.stock_quantity <= 0);
}

export default function WalkInPos({ onContinue }) {
  const { experiences, rigs } = useBookingConfig();
  const [picked, setPicked] = useState(null); // { key, name, price, rigKey, rigName }
  const [cart, setCart] = useState({}); // posItem.id -> { ...item, qty }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerExp, setPickerExp] = useState(null);
  const [activeTab, setActiveTab] = useState('experiences');
  // Extra time sold on a bay that is already mid-session.
  const [extensions, setExtensions] = useState([]);
  // Sized merch tile awaiting a size choice (null = no picker open).
  const [sizeTile, setSizeTile] = useState(null);
  // Last scan result, shown briefly at the top of the till so staff get instant
  // confirmation of what the gun added — or that it matched nothing.
  const [scanNotice, setScanNotice] = useState(null);
  // The scanner listener is bound once; this ref always points at the current
  // handler so a re-render never leaves the gun talking to a stale basket.
  const scanHandlerRef = useRef(null);

  const expList = useMemo(
    () => Object.values(experiences || {})
      .filter((e) => e.active !== false)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [experiences]
  );

  // Concession tiles are pulled live from Square products by reporting category
  // (Beverages → Drinks, Snacks → Snacks, Merch → Merch).
  const { data: concessionProducts = [], isLoading: itemsLoading } = useQuery({
    queryKey: ['pos-concessions'],
    queryFn: () => base44.entities.Product.filter(
      { reporting_category: { $regex: 'beverage|snack|merch', $options: 'i' } },
      '-name',
      200
    ),
    // Stock is taken out of Square by sales on the website and at the counter, so
    // the concession tiles re-read in the background rather than going stale.
    refetchInterval: 30000,
    staleTime: 10000,
  });
  const activeItems = (concessionProducts || [])
    .filter((p) => p.published !== false && p.sellable !== false && p.in_stock !== false && p.price != null && p.pos_visible !== false)
    .map((p) => {
      // Variations of one item would otherwise reach the till as identical tiles,
      // so a variant carries its own option (e.g. the biscuit type) in its label.
      const base = p.customer_facing_name || p.name;
      const option = p.is_variant ? (p.option_value || p.variation_name || '') : '';
      return {
        id: p.id,
        name: option ? `${base} — ${option}` : base,
        price: p.price,
        image_url: p.image_url,
        _rc: p.reporting_category,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const grouped = activeItems.reduce((acc, it) => {
    const cat = posCatFor(it._rc);
    if (!cat) return acc;
    (acc[cat] = acc[cat] || []).push(it);
    return acc;
  }, {});

  // Merch sourcing is deliberately looser than Drinks/Snacks: stock is checked
  // per size rather than per item, so a sold-out size still reaches the picker
  // to be shown greyed out. A tile only disappears once every size is gone.
  const merchProducts = useMemo(
    () => (concessionProducts || []).filter(
      (p) =>
        p.published !== false &&
        p.sellable !== false &&
        p.price != null &&
        p.pos_visible !== false &&
        posCatFor(p.reporting_category) === 'merch',
    ),
    [concessionProducts],
  );

  // Apparel is sold as size variations of one product, so variants sharing a
  // parent item collapse into a single tile that opens the size picker. Fixed
  // accessories are single records and add straight to the order.
  const merchTiles = useMemo(() => {
    const groups = new Map();
    for (const p of merchProducts) {
      const key = p.square_parent_item_id || p.square_item_id || p.id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    }
    const tiles = [];
    for (const variants of groups.values()) {
      const options = variants.map((v) => ({
        id: v.id,
        label: v.option_value || v.variation_name || v.name,
        price: Number(v.price),
        available: isSellable(v),
      }));
      if (!options.some((o) => o.available)) continue;
      const first = variants[0];
      tiles.push({
        id: first.id,
        name: first.customer_facing_name || first.name,
        price: Math.min(...variants.map((v) => Number(v.price))),
        image_url: first.image_url,
        options: variants.length > 1 ? options : null,
      });
    }
    return tiles.sort((a, b) => a.name.localeCompare(b.name));
  }, [merchProducts]);

  // Every line the basket can re-add, keyed by product id. A sized line keeps its
  // size in the label so the + control adds back the exact same line.
  const addableById = useMemo(() => {
    const map = new Map();
    for (const it of activeItems) map.set(it.id, it);
    for (const p of merchProducts) {
      const base = p.customer_facing_name || p.name;
      const size = p.option_value || p.variation_name;
      map.set(p.id, {
        id: p.id,
        name: size ? `${base} — ${size}` : base,
        price: Number(p.price),
        image_url: p.image_url,
      });
    }
    return map;
  }, [activeItems, merchProducts]);

  // Unit barcodes the till will accept, mapped to the line they add. Only items
  // that are POS-visible, sellable, in stock and currently on a tile are included,
  // so a hidden or discontinued line can never be scanned into a basket. Case /
  // outer-box codes are held in the product notes, never in the GTIN field, so a
  // wholesale box code can never resolve to a sale.
  const barcodeLookup = useMemo(() => {
    const map = new Map();
    for (const p of concessionProducts || []) {
      if (!p.gtin || p.pos_visible === false || p.published === false || p.sellable === false) continue;
      if (p.price == null || !isSellable(p) || !addableById.has(p.id)) continue;
      map.set(String(p.gtin).trim(), p.id);
    }
    return map;
  }, [concessionProducts, addableById]);

  // Category tabs — Experiences always first, then whichever concession
  // categories actually hold items. Only one category shows at a time.
  // Merch always holds a pill of its own, even with nothing published yet, so the
  // channel is visible from day one and lights up as lines are added.
  const tabs = [
    { key: 'experiences', label: 'EXPERIENCES', icon: Check, count: expList.length },
    ...CAT_ORDER.filter((c) => c === 'merch' || grouped[c]?.length).map((c) => ({
      key: c,
      label: CAT_META[c].label,
      icon: CAT_META[c].icon,
      count: c === 'merch' ? merchTiles.length : grouped[c].length,
    })),
  ];

  // One tile list per active tab — sized merch tiles carry `options` and open the
  // size picker; everything else adds on a direct tap.
  const activeTiles = useMemo(() => {
    if (activeTab === 'merch') return merchTiles;
    return (grouped[activeTab] || []).map((it) => ({
      id: it.id,
      name: it.name,
      price: it.price,
      image_url: it.image_url,
      options: null,
    }));
  }, [activeTab, grouped, merchTiles]);

  useEffect(() => {
    if (!tabs.some((t) => t.key === activeTab)) setActiveTab('experiences');
  }, [activeTab, tabs]);

  const { data: bookings = [] } = useQuery({
    queryKey: ['walkin-today-bookings', todayISO()],
    queryFn: () => base44.entities.Booking.filter({ date: todayISO() }, '-created_date', 200),
    refetchInterval: 15000,
  });

  const bookedRigKeys = useMemo(() => {
    const slot = currentSlot();
    return (bookings || [])
      .filter((b) => b.status !== 'cancelled' && b.time_slot === slot && b.rig_type)
      .map((b) => b.rig_type);
  }, [bookings]);

  const addons = Object.values(cart).map(({ id, name, price, qty }) => ({ id, name, price, qty }));
  const addonsTotal = addons.reduce((s, a) => s + a.price * a.qty, 0);
  const expPrice = picked?.price || 0;
  const extTotal = sumExtensions(extensions);
  const total = expPrice + addonsTotal + extTotal;

  const openPicker = (exp) => { setPickerExp(exp); setPickerOpen(true); };

  const handlePickRig = (rigKey) => {
    const rig = rigs[rigKey] || { name: rigKey };
    setPicked({
      key: pickerExp.key,
      name: pickerExp.name,
      price: pickerExp.price,
      rigKey,
      rigName: rig.name || rigKey,
    });
    setPickerOpen(false);
    setPickerExp(null);
  };

  const addAddon = (item) => {
    setCart((c) => {
      const ex = c[item.id];
      if (!ex) return { ...c, [item.id]: { ...item, qty: 1 } };
      return { ...c, [item.id]: { ...ex, qty: ex.qty + 1 } };
    });
  };
  const decAddon = (id) => {
    setCart((c) => {
      const ex = c[id];
      if (!ex) return c;
      if (ex.qty <= 1) { const { [id]: _, ...rest } = c; return rest; }
      return { ...c, [id]: { ...ex, qty: ex.qty - 1 } };
    });
  };

  // An extension is bound to its bay the moment it is added, so a basket holding
  // nothing but extensions is already complete and can go straight to payment.
  const addExtension = (def, bay) => {
    setExtensions((list) => [
      ...list,
      {
        key: def.key,
        name: def.name,
        price: def.price,
        qty: 1,
        minutes: def.minutes,
        target_rig_id: bay.id,
        rig_name: bay.name || '',
        bay_number: bay.bay_number || '',
      },
    ]);
  };
  const removeExtension = (index) => setExtensions((list) => list.filter((_, i) => i !== index));
  const rebindExtension = (index, bay) =>
    setExtensions((list) =>
      list.map((line, i) =>
        i === index
          ? { ...line, target_rig_id: bay.id, rig_name: bay.name || '', bay_number: bay.bay_number || '' }
          : line,
      ),
    );

  // A scan lands here: the line goes straight into the order, bumping the quantity
  // if it is already in the basket, and a brief notice names what was added.
  scanHandlerRef.current = (code) => {
    const productId = barcodeLookup.get(code);
    const line = productId ? addableById.get(productId) : null;
    if (!line) {
      setScanNotice({ type: 'error', title: 'BARCODE NOT RECOGNISED', detail: code });
      return;
    }
    addAddon(line);
    setScanNotice({ type: 'ok', title: 'ADDED TO ORDER', detail: line.name });
  };

  // Keyboard-wedge scanner: the gun types the barcode and presses Enter. The till
  // listens for that keystroke burst anywhere on the screen, so staff scan straight
  // into the order without tapping a field first. A pause longer than a scanner's
  // burst means a person is typing, so the partial buffer is discarded.
  useEffect(() => {
    let buffer = '';
    let lastKeyAt = 0;
    const onKeyDown = (e) => {
      const el = e.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      const now = Date.now();
      if (now - lastKeyAt > 120) buffer = '';
      lastKeyAt = now;
      if (e.key === 'Enter') {
        const code = buffer.trim();
        buffer = '';
        if (code.length >= 6) scanHandlerRef.current?.(code);
        return;
      }
      if (e.key.length === 1) buffer += e.key;
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Clear the scan notice shortly after it appears so the till stays uncluttered.
  useEffect(() => {
    if (!scanNotice) return;
    const t = setTimeout(() => setScanNotice(null), 2600);
    return () => clearTimeout(t);
  }, [scanNotice]);

  // A basket needs no experience: drinks, snacks and merch sell on their own, and
  // extra time on a rig already running is a sale in its own right.
  const canContinue = (picked && picked.rigKey) || extensions.length > 0 || addons.length > 0;

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-start">
        {/* LEFT: category tabs + the active category grid */}
        <div className="flex-1 min-w-0 px-6 lg:px-8 py-6">
          {/* Scan feedback — the gun's confirmation, or a clear mismatch warning */}
          {scanNotice && (
            <div
              className={`mb-4 flex items-center gap-3 border-2 px-4 py-3 ${
                scanNotice.type === 'ok'
                  ? 'border-emerald-500/50 bg-emerald-500/10'
                  : 'border-primary bg-primary/10'
              }`}
            >
              {scanNotice.type === 'ok'
                ? <Check className="w-5 h-5 text-emerald-400 shrink-0" />
                : <AlertTriangle className="w-5 h-5 text-primary shrink-0" />}
              <div className="min-w-0">
                <p className={`font-black text-xs tracking-widest ${scanNotice.type === 'ok' ? 'text-emerald-400' : 'text-primary'}`}>
                  {scanNotice.title}
                </p>
                <p className="text-white/70 text-sm font-bold truncate">{scanNotice.detail}</p>
              </div>
            </div>
          )}
          {/* Category tabs — one category at a time, tablet-sized targets */}
          <div className="flex flex-wrap gap-2 mb-5">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`flex items-center gap-2 border-2 px-5 py-3.5 font-black text-xs tracking-widest transition-colors ${
                    active
                      ? 'border-primary bg-primary text-white'
                      : 'border-white/10 bg-[#111115] text-white/50 hover:text-white hover:border-white/30'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                  <span className={`text-[10px] font-bold ${active ? 'text-white/70' : 'text-white/30'}`}>{t.count}</span>
                </button>
              );
            })}
          </div>

          {activeTab === 'experiences' && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {expList.map((e) => {
              const isPicked = picked?.key === e.key;
              return (
                <button
                  key={e.key}
                  onClick={() => openPicker(e)}
                  className={`flex flex-col text-left p-5 border-2 transition-all min-h-[170px] ${
                    isPicked
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/40'
                      : 'border-white/10 bg-[#111115] hover:border-white/30 hover:ring-2 hover:ring-white/10'
                  }`}
                >
                  {/* Top row: age tag pill + bold red price */}
                  <div className="flex items-center justify-between gap-3 w-full">
                    {e.tag
                      ? <span className="text-[10px] font-bold tracking-widest text-white/60 border border-white/15 px-2.5 py-1">{e.tag}</span>
                      : <span />}
                    <span className="text-primary font-black text-2xl shrink-0">£{e.price}</span>
                  </div>

                  {/* Middle: large bold title */}
                  <h3 className="text-white font-black text-xl sm:text-2xl uppercase leading-tight mt-4">{e.name}</h3>

                  {/* Bottom: duration badge */}
                  <div className="mt-auto pt-4">
                    <span className="inline-flex items-center gap-1.5 text-white/50 text-xs font-bold tracking-widest border border-white/10 px-2.5 py-1.5">
                      <Clock className="w-3.5 h-3.5" /> {e.duration_minutes} MINS
                    </span>
                  </div>

                  {isPicked && (
                    <div className="mt-3 flex items-center gap-1.5 text-primary text-[10px] font-bold tracking-widest border-t border-white/5 pt-2">
                      <Cpu className="w-3.5 h-3.5" /> RIG: {picked.rigName}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Session extensions sold as service items, in the same catalogue view
              as the experiences they add time to. */}
          <SessionExtensionTiles onAdd={addExtension} />
          </>
          )}

          {activeTab !== 'experiences' && (
            itemsLoading ? (
              <div className="flex items-center gap-2 text-white/40 text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading items…</div>
            ) : activeTiles.length === 0 ? (
              <div className="border border-dashed border-white/10 bg-[#111115] py-16 px-6 text-center">
                <Tag className="w-8 h-8 text-white/10 mx-auto mb-3" />
                <p className="text-white/40 text-sm font-black tracking-widest">NO MERCHANDISE YET</p>
                <p className="text-white/25 text-xs mt-2 max-w-xs mx-auto leading-relaxed">
                  Items appear here as soon as they're published to the Merch category.
                </p>
              </div>
            ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 items-start">
                      {activeTiles.map((tile) => {
                        const inCart = tile.options
                          ? tile.options.reduce((s, o) => s + (cart[o.id]?.qty || 0), 0)
                          : (cart[tile.id]?.qty || 0);
                        return (
                          <button
                            key={tile.id}
                            onClick={() => (tile.options ? setSizeTile(tile) : addAddon(addableById.get(tile.id)))}
                            className={`relative w-full flex flex-col items-center text-center p-2 border transition-all ${inCart ? 'border-primary bg-primary/10' : 'border-white/10 bg-[#111115] hover:border-white/30'}`}
                          >
                            <div className="flex items-center justify-center h-[130px] w-full p-2">
                              {tile.image_url && (
                                <img
                                  src={tile.image_url}
                                  alt={tile.name}
                                  className="max-h-[110px] max-w-[80%] w-auto h-auto object-contain block mx-auto"
                                />
                              )}
                            </div>
                            <div className="flex flex-col items-center justify-center min-h-[54px] w-full px-2 py-1">
                              <p className="text-sm font-semibold text-white text-center leading-tight">{tile.name}</p>
                              <p className="text-base font-bold text-red-500 text-center mt-1">£{Number(tile.price).toFixed(2)}</p>
                            </div>
                            {tile.options && (
                              <span className="pb-1 text-white/30 text-[9px] font-bold tracking-widest">SELECT SIZE</span>
                            )}
                            {inCart > 0 && (
                              <span className="absolute top-1.5 right-1.5 bg-primary text-white text-[10px] font-black w-5 h-5 flex items-center justify-center">{inCart}</span>
                            )}
                          </button>
                        );
                      })}
            </div>
            )
          )}
        </div>

        {/* RIGHT: order basket — docked to the right edge, full height */}
        <aside className="w-full lg:w-[32%] lg:min-w-[360px] lg:max-w-[520px] border-t lg:border-t-0 lg:border-l border-zinc-800 bg-[#141416] lg:sticky lg:top-[72px] lg:h-[calc(100vh-72px)] flex flex-col">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between shrink-0">
              <p className="text-white font-black text-sm uppercase tracking-tight">Current Order</p>
              <span className="text-primary text-[10px] font-bold tracking-widest">{currentSlot()} · TODAY</span>
            </div>

            <div className="flex-1 overflow-y-auto">

            <div className="p-4 space-y-3 min-h-[120px]">
              {!picked && extensions.length === 0 && addons.length === 0 ? (
                <p className="text-white/30 text-xs text-center py-6">
                  Tap an experience, add drinks, snacks or merch, or extend an active session.
                </p>
              ) : (
                picked && (
                  <div className="flex items-center justify-between border border-primary/20 bg-primary/5 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-white font-bold text-xs uppercase truncate">{picked.name}</p>
                      <p className="text-primary text-[10px] font-bold tracking-widest">RIG · {picked.rigName}</p>
                    </div>
                    <p className="text-primary font-black text-sm shrink-0">£{picked.price.toFixed(2)}</p>
                  </div>
                )
              )}

              {addons.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  {addons.map((a) => (
                    <div key={a.id} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <button onClick={() => decAddon(a.id)} className="w-5 h-5 flex items-center justify-center border border-white/10 text-white/50 hover:text-white hover:border-white/30 shrink-0">
                          {cart[a.id]?.qty <= 1 ? <Trash2 className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                        </button>
                        <span className="text-white/50 w-5 text-center">{a.qty}</span>
                        <button onClick={() => addAddon(addableById.get(a.id))} className="w-5 h-5 flex items-center justify-center border border-white/10 text-white/50 hover:text-white hover:border-white/30 shrink-0">
                          <Plus className="w-3 h-3" />
                        </button>
                        <span className="text-white/70 truncate ml-1">{a.name}</span>
                      </div>
                      <span className="text-white/60 shrink-0">£{(a.price * a.qty).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              <SessionExtensionLines
                extensions={extensions}
                onRemove={removeExtension}
                onRebind={rebindExtension}
              />
            </div>

            <div className="px-4 py-3 border-t border-white/10 space-y-1">
              <div className="flex items-center justify-between text-xs text-white/40">
                <span>Experience</span><span>£{picked ? picked.price.toFixed(2) : '0.00'}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-white/40">
                <span>Add-ons</span><span>£{addonsTotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-white/40">
                <span>Session extensions</span><span>£{extTotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-white font-bold text-sm">TOTAL</span>
                <span className="text-primary font-black text-xl">£{total.toFixed(2)}</span>
              </div>
            </div>
            </div>

            <div className="p-4 border-t border-white/10 shrink-0">
              <button
                disabled={!canContinue}
                onClick={() => onContinue({
                  experienceType: picked?.key || '',
                  experienceName: picked?.name || '',
                  rigType: picked?.rigKey || '',
                  rigName: picked?.rigName || '',
                  // product_id travels with the line so the payment step can
                  // take the concession out of Square stock.
                  addons: addons.map(({ id, name, price, qty }) => ({ name, price, qty, product_id: id })),
                  addonsTotal,
                  // Extensions are service time — no stock to move, and already
                  // bound to the bay whose timer they extend.
                  extensions: extensions.map(({ name, price, qty, target_rig_id, minutes }) => ({
                    name,
                    price,
                    qty,
                    target_rig_id,
                    extension_minutes: minutes,
                  })),
                  extensionsTotal: extTotal,
                  amount: total,
                })}
                className="w-full bg-primary text-white font-black text-sm tracking-widest py-4 disabled:opacity-30 flex items-center justify-center gap-2 hover:bg-primary/90"
              >
                CONTINUE <ArrowRight className="w-4 h-4" />
              </button>
              {!canContinue && (
                <p className="text-white/30 text-[10px] text-center mt-2">
                  Select an experience, assign a rig, add concessions, or extend an active session.
                </p>
              )}
            </div>
        </aside>
      </div>

      <WalkInRigPicker
        open={pickerOpen}
        experience={pickerExp}
        bookedRigKeys={bookedRigKeys}
        onSelect={handlePickRig}
        onClose={() => { setPickerOpen(false); setPickerExp(null); }}
      />

      <WalkInSizePicker
        open={!!sizeTile}
        tile={sizeTile}
        onSelect={(optionId) => { addAddon(addableById.get(optionId)); setSizeTile(null); }}
        onClose={() => setSizeTile(null)}
      />
    </>
  );
}